import { useState, useEffect, useRef } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Html5Qrcode } from "html5-qrcode";
import api, { apiErr } from "@/lib/api";
import { Icon } from "@/lib/ui";
import { Loader2 } from "lucide-react";

function extractToken(text) {
  if (!text) return null;
  const m = text.match(/\/t\/([A-Za-z0-9_-]+)/);
  if (m) return m[1];
  // If a bare token was encoded
  if (/^[A-Za-z0-9_-]{16,}$/.test(text.trim())) return text.trim();
  return null;
}

export default function ClaimTag() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const [token, setToken] = useState(sp.get("token") || "");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [scanning, setScanning] = useState(false);
  const scannerRef = useRef(null);

  useEffect(() => {
    if (token) api.get(`/public/t/${token}`).then((r) => setPreview(r.data.data)).catch(() => {});
  }, [token]);

  const stopScanner = async () => {
    if (scannerRef.current) {
      try { await scannerRef.current.stop(); await scannerRef.current.clear(); } catch {}
      scannerRef.current = null;
    }
    setScanning(false);
  };

  const startScanner = async () => {
    setScanning(true);
    await new Promise((r) => setTimeout(r, 100));
    try {
      const qr = new Html5Qrcode("qr-reader");
      scannerRef.current = qr;
      await qr.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 240, height: 240 } },
        async (decoded) => {
          const t = extractToken(decoded);
          if (t) {
            await stopScanner();
            setToken(t);
            toast.success("Tag scanned!");
          }
        },
        () => {}
      );
    } catch (e) {
      setScanning(false);
      toast.error("Could not access camera. Please allow camera permission.");
    }
  };

  useEffect(() => () => { stopScanner(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/tags/claim", { public_token: token.trim(), auth_code: code.trim() });
      toast.success("Tag successfully activated!");
      nav(`/app/tags/${data.data.id}`, { replace: true });
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-md mx-auto px-6 py-8">
        <Link to="/app" className="tap inline-flex items-center gap-1.5 text-sm font-semibold text-muted-foreground mb-8">
          <Icon name="ArrowLeft" className="w-4 h-4" /> Back
        </Link>
        <div className="animate-rise">
          <div className="w-14 h-14 rounded-2xl bg-primary text-primary-foreground grid place-items-center mb-5">
            <Icon name="ShieldCheck" className="w-7 h-7" />
          </div>
          <h1 className="font-display font-black text-3xl tracking-tight">Add a tag</h1>
          <p className="text-muted-foreground mt-2">Scan the QR on your tag, then enter its authentication code to prove ownership.</p>

          {!token ? (
            <div className="mt-6">
              {scanning ? (
                <div className="rounded-2xl overflow-hidden border border-border">
                  <div id="qr-reader" className="w-full" />
                  <button data-testid="stop-scan" onClick={stopScanner} className="tap w-full py-3 font-semibold text-status-lost border-t border-border">Cancel</button>
                </div>
              ) : (
                <button data-testid="start-scan" onClick={startScanner} className="tap w-full rounded-2xl border-2 border-dashed border-border py-12 flex flex-col items-center gap-3 hover:bg-secondary/50">
                  <div className="w-14 h-14 rounded-2xl bg-primary text-primary-foreground grid place-items-center"><Icon name="ScanLine" className="w-7 h-7" /></div>
                  <span className="font-bold">Open camera to scan</span>
                  <span className="text-sm text-muted-foreground">Point at the QR code on your tag</span>
                </button>
              )}
            </div>
          ) : (
            <>
              {preview?.category && (
                <div className="mt-5 rounded-2xl border border-border p-4 flex items-center gap-3">
                  <Icon name={preview.category.icon} className="w-5 h-5" />
                  <div className="flex-1">
                    <p className="font-semibold">{preview.category.name} tag</p>
                    <p className="text-sm text-muted-foreground font-mono">{preview.tag_number}</p>
                  </div>
                  <button data-testid="rescan" onClick={() => { setToken(""); setPreview(null); }} className="tap text-sm font-semibold text-status-active">Rescan</button>
                </div>
              )}
              <form onSubmit={submit} className="space-y-4 mt-6" data-testid="claim-form">
                <label className="block">
                  <span className="text-sm font-semibold">Authentication code</span>
                  <input data-testid="claim-code" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} required placeholder="XXXX-XXXX" className="mt-1.5 w-full h-14 px-4 rounded-xl border border-input font-mono text-lg tracking-widest focus:outline-none focus:ring-2 focus:ring-ring" />
                </label>
                <button data-testid="claim-submit" disabled={busy} className="tap w-full h-14 rounded-full bg-primary text-primary-foreground font-bold flex items-center justify-center gap-2 disabled:opacity-60">
                  {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Verify &amp; claim <Icon name="ArrowRight" className="w-4 h-4" /></>}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
